import React from 'react';
import { Loader2 } from 'lucide-react';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost';
  size?: 'sm' | 'md' | 'lg';
  loading?: boolean;
  children: React.ReactNode;
  as?: 'button' | 'span';
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'primary',
      size = 'md',
      loading = false,
      disabled,
      className = '',
      children,
      as = 'button',
      ...props
    },
    ref
  ) => {
    const baseStyles =
      'inline-flex items-center justify-center gap-2 font-medium rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed';

    const variants = {
      primary:
        'bg-secondary-500 text-white hover:bg-secondary-600 focus:ring-secondary-500 shadow-sm',
      secondary:
        'bg-primary-500 text-white hover:bg-primary-600 focus:ring-primary-500 shadow-sm',
      outline:
        'border-2 border-neutral-300 text-neutral-700 hover:bg-neutral-50 focus:ring-secondary-500',
      ghost: 'text-neutral-700 hover:bg-neutral-100 focus:ring-secondary-500',
    };

    const sizes = {
      sm: 'px-3 py-1.5 text-sm',
      md: 'px-4 py-2 text-base',
      lg: 'px-6 py-3 text-lg',
    };

    const Component = as;

    return (
      <Component
        ref={as === 'button' ? ref : undefined}
        disabled={disabled || loading}
        className={`${baseStyles} ${variants[variant]} ${sizes[size]} ${className}`}
        {...(as === 'button' ? props : {})}
      >
        {loading && <Loader2 className="w-4 h-4 animate-spin" />}
        {children}
      </Component>
    );
  }
);

Button.displayName = 'Button';

export default Button;
